import React, { useState, useEffect, useRef } from "react";
import {
  Box, Typography, Button, Chip, Dialog, DialogTitle, DialogContent,
  DialogActions, RadioGroup, FormControlLabel, Radio, CircularProgress,
  Tooltip, Switch, Popover
} from "@mui/material";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import api from "../../api/axios";
import { loadRazorpayScript, openRazorpay } from "../../utils/razorpay";
import { useSnackbar } from "../../utils/SnackbarProvider";

/* ── Plan definitions (independently configurable from agent plans) ── */
const PLANS = [
  { id: "3_MONTHS",  label: "3 Months",  price: 300, desc: "₹300/qtr — Best value" },
  { id: "6_MONTHS",  label: "6 Months",  price: 570, desc: "₹570/half-year — 5% discount" },
  { id: "12_MONTHS", label: "12 Months", price: 960, desc: "₹960/year — 20% discount" },
];

const PLAN_LABELS = {
  "3_MONTHS": "3 Months",
  "6_MONTHS": "6 Months",
  "12_MONTHS": "12 Months",
};

const getDaysLeft = (expiresAt) => {
  if (!expiresAt) return null;
  const diff = new Date(expiresAt) - new Date();
  const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
  return days > 0 ? days : 0;
};

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("en-IN", {
    day: "numeric", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
};

const VendorSponsorshipCard = ({
  sponsorshipStatus = "NONE",
  sponsorshipExpiresAt,
  vendorId,
  compact = false,
  onApplied
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState("3_MONTHS");
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  /* ── Sponsorship details for tooltip ── */
  const [sponsorshipDetails, setSponsorshipDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [autoRenew, setAutoRenew] = useState(true);
  const [togglingAutoRenew, setTogglingAutoRenew] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const detailsOpen = Boolean(anchorEl);
  const handleDetailsOpen = (event) => setAnchorEl(event.currentTarget);
  const handleDetailsClose = () => setAnchorEl(null);

  const sponsorshipFetchedRef = useRef(false);
  const { showSnackbar } = useSnackbar();

  useEffect(() => {
    if ((sponsorshipStatus === "ACTIVE" || sponsorshipStatus === "EXPIRED") && vendorId && !sponsorshipFetchedRef.current) {
      sponsorshipFetchedRef.current = true;
      fetchSponsorshipDetails();
    }
  }, [sponsorshipStatus, vendorId]);

  const fetchSponsorshipDetails = async () => {
    try {
      setDetailsLoading(true);
      const { data } = await api.get(`/vendor/${vendorId}/sponsorship-details`);
      if (data.success) {
        setSponsorshipDetails(data);
        setAutoRenew(data.sponsorshipAutoRenew);
      }
    } catch (err) {
      console.warn("Failed to fetch sponsorship details:", err);
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleToggleAutoRenew = async () => {
    const previousState = autoRenew;
    setAutoRenew(!autoRenew);
    setTogglingAutoRenew(true);
    try {
      const { data } = await api.post(`/vendor/${vendorId}/toggle-auto-renew`);
      if (data.success) {
        setAutoRenew(data.sponsorshipAutoRenew);
        showSnackbar(data.message, 'success');
      } else {
        setAutoRenew(previousState);
        showSnackbar(data.message || 'Failed to toggle auto-renew', 'error');
      }
    } catch (err) {
      setAutoRenew(previousState);
      showSnackbar(err?.response?.data?.message || 'Failed to toggle auto-renew', 'error');
      console.error("Toggle auto-renew error:", err);
    } finally {
      setTogglingAutoRenew(false);
    }
  };

  const vendorUser = JSON.parse(localStorage.getItem("user") || "{}");
  const isOwnVendorPage = vendorUser?.role === "vendor" && vendorUser?._id === vendorId;

  const handleProceedPayment = async () => {
    try {
      setPaymentLoading(true);
      setErrorMessage("");

      const { data } = await api.post(`/vendor/${vendorId}/create-sponsorship-subscription`, {
        plan: selectedPlan,
      });

      if (!data.success) {
        setErrorMessage(data.message || "Failed to initiate subscription");
        setPaymentLoading(false);
        return;
      }

      await loadRazorpayScript();

      const plan = PLANS.find(p => p.id === selectedPlan);

      openRazorpay({
        amount: data.amount,
        subscription_id: data.subscriptionId,
        description: `Vendor Sponsorship - ${plan?.label || selectedPlan}`,
        themeColor: "#f5a623",
        onSuccess: async (response) => {
          try {
            const verifyRes = await api.post(`/vendor/${vendorId}/verify-sponsorship-subscription`, {
              subscriptionId: data.subscriptionId,
              plan: selectedPlan,
              razorpay_payment_id: response.razorpay_payment_id,
            });

            if (verifyRes.data.success) {
              setSuccessMessage(verifyRes.data.message);
              setPaymentLoading(false);
              setTimeout(() => {
                setDialogOpen(false);
                setSuccessMessage("");
                onApplied?.();
              }, 2500);
            } else {
              setErrorMessage("Subscription verification failed. Please contact support.");
              setPaymentLoading(false);
            }
          } catch (err) {
            setErrorMessage(err.response?.data?.message || "Verification failed");
            setPaymentLoading(false);
          }
        },
        onDismiss: () => setPaymentLoading(false),
      });
    } catch (err) {
      console.error("Vendor sponsorship payment error:", err);
      setErrorMessage(err.response?.data?.message || err.message || "Please try again");
      setPaymentLoading(false);
    }
  };

  const handleOpenDialog = () => {
    setSuccessMessage("");
    setErrorMessage("");
    setDialogOpen(true);
  };
  const handleCloseDialog = () => {
    if (!paymentLoading && !successMessage) setDialogOpen(false);
  };

  const daysLeft = getDaysLeft(sponsorshipExpiresAt);

  /* ── Tooltip content for ACTIVE/EXPIRED sponsorship ── */
  const tooltipContent = (
    <Box sx={{ p: 1, minWidth: 240 }}>
      {detailsLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={20} />
        </Box>
      ) : sponsorshipDetails ? (
        <>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
            🚀 Sponsorship Details
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, fontSize: '0.8rem' }}>
            <Typography variant="caption">
              <strong>Plan:</strong> {PLAN_LABELS[sponsorshipDetails.sponsorshipPlan] || sponsorshipDetails.sponsorshipPlan}
            </Typography>
            <Typography variant="caption">
              <strong>Activated:</strong> {formatDate(sponsorshipDetails.sponsorshipActivatedAt)}
            </Typography>
            <Typography variant="caption">
              <strong>Expires:</strong> {formatDate(sponsorshipDetails.sponsorshipExpiresAt)}
            </Typography>
            <Typography variant="caption" sx={{ color: daysLeft > 0 ? '#2e7d32' : '#d32f2f' }}>
              <strong>Status:</strong> {daysLeft > 0 ? `${daysLeft} days remaining` : 'Expired'}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
              <Typography variant="caption">
                <strong>Auto-Renew:</strong>
              </Typography>
              <Switch
                size="small"
                checked={autoRenew}
                onChange={handleToggleAutoRenew}
                disabled={togglingAutoRenew}
                sx={{ '& .MuiSwitch-thumb': { backgroundColor: autoRenew ? '#f5a623' : '#999' } }}
              />
              {togglingAutoRenew && <CircularProgress size={12} />}
            </Box>
            <Typography variant="caption" sx={{ color: '#666', mt: 0.5 }}>
              {autoRenew
                ? "Plan will auto-renew via Razorpay subscription"
                : "Plan will expire and not renew"}
            </Typography>
          </Box>
        </>
      ) : (
        <Typography variant="caption">Unable to load details</Typography>
      )}
    </Box>
  );

  // ✅ ACTIVE → Sponsored badge with tooltip
  if (sponsorshipStatus === "ACTIVE") {
    const label = daysLeft !== null
      ? (daysLeft > 0 ? `Sponsored Vendor | ${daysLeft} Days Left` : "Sponsored Vendor | Expiring")
      : "Sponsored Vendor";
    return (
      <>
        <Tooltip
          title={tooltipContent}
          arrow
          placement="bottom-end"
          componentsProps={{ tooltip: { sx: { bgcolor: '#fff', color: '#333', boxShadow: '0 4px 20px rgba(0,0,0,0.15)', border: '1px solid #e0e0e0', maxWidth: 300 } } }}
        >
          <Chip
            icon={<WorkspacePremiumIcon />}
            label={label}
            onClick={handleDetailsOpen}
            sx={{
              bgcolor: "#fff4e5",
              color: "#b26a00",
              fontWeight: 600,
              fontSize: 12,
              cursor: 'pointer',
            }}
          />
        </Tooltip>
        <Popover
          open={detailsOpen}
          anchorEl={anchorEl}
          onClose={handleDetailsClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          slotProps={{ paper: { sx: { borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.15)', border: '1px solid #e0e0e0', maxWidth: 300 } } }}
        >
          {tooltipContent}
        </Popover>
      </>
    );
  }

  // ✅ PENDING → Under Review
  if (sponsorshipStatus === "PENDING") {
    return (
      <Chip
        icon={<HourglassEmptyIcon />}
        label="Under Review"
        sx={{
          bgcolor: "#e3f2fd",
          color: "#1565c0",
          fontWeight: 600,
          fontSize: 12
        }}
      />
    );
  }

  // ✅ EXPIRED → chip with tooltip that opens dialog
  if (sponsorshipStatus === "EXPIRED" && isOwnVendorPage) {
    return (
      <>
        <Tooltip
          title={tooltipContent}
          arrow
          placement="bottom-end"
          componentsProps={{ tooltip: { sx: { bgcolor: '#fff', color: '#333', boxShadow: '0 4px 20px rgba(0,0,0,0.15)', border: '1px solid #e0e0e0', maxWidth: 300 } } }}
        >
          <Chip
            icon={<WorkspacePremiumIcon />}
            label="Renew Sponsorship"
            onClick={handleOpenDialog}
            sx={{
              bgcolor: "#fff4e5",
              color: "#b26a00",
              fontWeight: 600,
              fontSize: 12,
              cursor: 'pointer',
              '&:hover': { bgcolor: '#ffedd5' }
            }}
          />
        </Tooltip>
        <Popover
          open={detailsOpen}
          anchorEl={anchorEl}
          onClose={handleDetailsClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          slotProps={{ paper: { sx: { borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.15)', border: '1px solid #e0e0e0', maxWidth: 300 } } }}
        >
          {tooltipContent}
        </Popover>
      </>
    );
  }

  // ✅ PENDING_PAYMENT → chip that opens dialog
  if (sponsorshipStatus === "PENDING_PAYMENT" && isOwnVendorPage) {
    return (
      <Chip
        icon={<WorkspacePremiumIcon />}
        label="Complete Payment"
        onClick={handleOpenDialog}
        sx={{
          bgcolor: "#fff4e5",
          color: "#b26a00",
          fontWeight: 600,
          fontSize: 12,
          cursor: 'pointer',
          '&:hover': { bgcolor: '#ffedd5' }
        }}
      />
    );
  }

  // ✅ NONE → hide for non-owners
  if (!isOwnVendorPage) {
    return null;
  }

  // ✅ NONE → Show promo box with Apply button
  return (
    <>
      <Box
        sx={{
          p: compact ? 2 : 3,
          borderRadius: 3,
          border: "1px solid #f0c14b",
          bgcolor: "#fffdf7",
          display: "flex",
          flexDirection: compact ? "row" : "column",
          alignItems: compact ? "center" : "flex-start",
          justifyContent: "space-between",
          gap: 2
        }}
      >
        {/* LEFT */}
        <Box sx={{ flex: 1 }}>
          <Typography fontWeight={700}>
            Become a Sponsored Seller 🚀
          </Typography>

          {!compact && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 0.5 }}
            >
              Increase visibility, boost sales, and earn customer trust with a
              sponsored badge.
            </Typography>
          )}
        </Box>

        {/* RIGHT */}
        <Button
          variant="contained"
          size="small"
          onClick={handleOpenDialog}
          sx={{
            bgcolor: "#ffd814",
            color: "#000",
            fontWeight: 600,
            "&:hover": { bgcolor: "#f7ca00" }
          }}
        >
          Apply
        </Button>
      </Box>

      {/* ── Plan Selection Dialog ── */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          🚀 Choose Your Sponsorship Plan
        </DialogTitle>

        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Sponsored vendors get priority visibility and increased customer trust.
            Plans auto-renew via Razorpay subscription.
          </Typography>

          {successMessage ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CheckCircleOutlineIcon sx={{ fontSize: 64, color: '#4caf50', mb: 2 }} />
              <Typography variant="h6" fontWeight={700} sx={{ color: '#2e7d32', mb: 1 }}>
                ✅ Sponsorship Activated!
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {successMessage}
              </Typography>
            </Box>
          ) : (
            <RadioGroup value={selectedPlan} onChange={(e) => setSelectedPlan(e.target.value)}>
              {PLANS.map((plan) => (
                <Box
                  key={plan.id}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 1.5,
                    p: 1.5, mb: 1,
                    border: '1px solid',
                    borderColor: selectedPlan === plan.id ? '#f5a623' : '#e0e0e0',
                    borderRadius: 2,
                    bgcolor: selectedPlan === plan.id ? '#fff8f0' : '#fff',
                    cursor: 'pointer',
                  }}
                  onClick={() => setSelectedPlan(plan.id)}
                >
                  <FormControlLabel
                    value={plan.id}
                    control={<Radio sx={{ '&.Mui-checked': { color: '#f5a623' } }} />}
                    label=""
                    sx={{ m: 0, p: 0 }}
                  />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" fontWeight={700}>
                      {plan.label}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {plan.desc}
                    </Typography>
                  </Box>
                  <Typography variant="h6" fontWeight={700} sx={{ color: '#b26a00' }}>
                    ₹{plan.price}
                  </Typography>
                </Box>
              ))}
            </RadioGroup>
          )}

          {errorMessage && (
            <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
              {errorMessage}
            </Typography>
          )}
        </DialogContent>

        {!successMessage && (
          <DialogActions sx={{ p: 2, pt: 0 }}>
            <Button onClick={handleCloseDialog} disabled={paymentLoading}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleProceedPayment}
              disabled={paymentLoading}
              sx={{
                bgcolor: '#f5a623', color: '#1a1a1a',
                fontWeight: 600,
                '&:hover': { bgcolor: '#e09515' },
                '&.Mui-disabled': { bgcolor: '#e0e0e0', color: '#999' },
              }}
            >
              {paymentLoading ? (
                <CircularProgress size={20} sx={{ color: '#1a1a1a' }} />
              ) : (
                `Subscribe ₹${PLANS.find(p => p.id === selectedPlan)?.price || 0}`
              )}
            </Button>
          </DialogActions>
        )}
      </Dialog>
    </>
  );
};

export default VendorSponsorshipCard;